import { create } from "zustand";

/**
 * State for the in-app tutorial: the spoken walkthrough of the interface that
 * bolds each icon as it is named.
 *
 * Deliberately separate from the exhibition session. The tutorial is available
 * to every user on any design, so it cannot hang off state that only exists for
 * a visitor at a trade show.
 *
 * Starting is expressed as a counter rather than a boolean, so pressing the
 * button again while a tutorial is already finished still registers as a fresh
 * request. A boolean would need clearing first and could be missed.
 */
interface TutorialStore {
  /** Incremented by each request to start. Chatbox watches this. */
  requestCount: number;
  /**
   * True while any narration is speaking — the tutorial or the exhibition
   * demonstration.
   *
   * Shared by both because it answers one question for the UI: is the assistant
   * mid-explanation? The tour controls hide while it is, since Replay and Skip
   * have nothing to offer someone who is being spoken to.
   */
  running: boolean;
  /**
   * Which narration the `running` flag belongs to.
   *
   * Both the interface walkthrough and the exhibition demonstration set
   * `running`, because the UI only needs to know that something is being said.
   * Anything that has to tell them apart needs this: a visitor asking for the
   * tutorial in the middle of the demonstration must be told the demonstration
   * is playing, not that a walkthrough already is — and must certainly not have
   * the demonstration cut off and replaced by one.
   */
  narration: "none" | "tutorial" | "tour";
  /** True once it has run to the end at least once this page load. */
  completed: boolean;
  request: () => void;
  setRunning: (running: boolean, narration?: "tutorial" | "tour") => void;
  markCompleted: () => void;
  reset: () => void;
}

export const useTutorialStore = create<TutorialStore>((set) => ({
  requestCount: 0,
  running: false,
  narration: "none",
  completed: false,
  request: () => set((state) => ({ requestCount: state.requestCount + 1 })),
  setRunning: (running: boolean, narration: "tutorial" | "tour" = "tutorial") =>
    set({ running, narration: running ? narration : "none" }),
  markCompleted: () => set({ running: false, narration: "none", completed: true }),
  reset: () => set({ running: false, narration: "none", completed: false }),
}));

/** Ask for the interface walkthrough to start. */
export function requestTutorial(): void {
  useTutorialStore.getState().request();
}
